// Shared helpers + status metadata for the Job Cards module.
// -----------------------------------------------------------------------------

// 8-state operational lifecycle (Feb 2026 simplification).
// Sequential flow:
//   draft → vehicle_received → approval_pending → work_in_progress
//         → quality_check → ready_for_delivery → closed
// cancelled is a terminal out-of-band state.
export const STATUS_META = {
  draft:              { label: "Draft",              chip: "bg-[#98A2B3]/20 text-[#475467]",  dot: "#98A2B3", order: 0 },
  vehicle_received:   { label: "Vehicle Received",   chip: "bg-[#a8dadc]/30 text-[#457b9d]",  dot: "#457b9d", order: 1 },
  approval_pending:   { label: "Approval Pending",   chip: "bg-[#FEF0C7] text-[#B54708]",     dot: "#F79009", order: 2 },
  work_in_progress:   { label: "Work In Progress",   chip: "bg-[#a8dadc]/20 text-[#1d3557]",  dot: "#1d3557", order: 3 },
  quality_check:      { label: "Quality Check",      chip: "bg-[#a8dadc]/30 text-[#457b9d]",  dot: "#457b9d", order: 4 },
  ready_for_delivery: { label: "Ready for Delivery", chip: "bg-[#12B76A]/15 text-[#0F9256]",  dot: "#12B76A", order: 5 },
  closed:             { label: "Closed",             chip: "bg-[#1d3557]/12 text-[#1d3557]",  dot: "#1d3557", order: 6 },
  cancelled:          { label: "Cancelled",          chip: "bg-[#e63946]/15 text-[#e63946]",  dot: "#e63946", order: 7 },
};

export const STATUS_ORDER = Object.keys(STATUS_META).sort(
  (a, b) => STATUS_META[a].order - STATUS_META[b].order,
);

// Legacy status names → new 8-state names. Backend already remaps on read,
// but we keep this here so legacy payloads render correctly on the client too.
export const LEGACY_STATUS_MAP = {
  open: "vehicle_received",
  in_progress: "work_in_progress",
  awaiting_approval: "approval_pending",
  awaiting_parts: "work_in_progress",
  completed: "ready_for_delivery",
  // Statuses retired in Feb 2026:
  inspection_pending: "vehicle_received",
  estimate_pending:   "approval_pending",
  waiting_for_parts:  "work_in_progress",
  work_completed:     "quality_check",
  delivered:          "closed",
};

export const remapStatus = (s) => LEGACY_STATUS_MAP[s] || s;

// Service-type master (matches Section 16 defaults; garages can add their own
// via the master-data admin later).
export const SERVICE_TYPES = [
  "General Service",
  "Periodic Maintenance",
  "Running Repair",
  "Breakdown Repair",
  "Accident Repair",
  "Denting and Painting",
  "AC Repair",
  "Electrical Repair",
  "Brake Repair",
  "Suspension Repair",
  "Wheel Alignment",
  "Tyre Service",
  "Battery Service",
  "Inspection",
  "Preventive Maintenance",
  "Other",
];

export const REPAIR_TYPES = [
  { key: "direct_customer", label: "Direct Customer" },
  { key: "insurance",       label: "Insurance" },
  { key: "warranty",        label: "Warranty" },
  { key: "internal",        label: "Internal" },
  { key: "other",           label: "Other" },
];

export const FUEL_LEVELS = [
  { key: "empty",          label: "Empty",  pct: 0 },
  { key: "quarter",        label: "1/4",    pct: 25 },
  { key: "half",           label: "1/2",    pct: 50 },
  { key: "three_quarter",  label: "3/4",    pct: 75 },
  { key: "full",           label: "Full",   pct: 100 },
];

export const PRIORITIES = [
  { key: "low",     label: "Low" },
  { key: "normal",  label: "Normal" },
  { key: "high",    label: "High" },
  { key: "urgent",  label: "Urgent" },
];

// Common complaint quick-picks (from Section 10).  Garage-specific ones
// can be added via customer complaints history in a later iteration.
export const COMMON_COMPLAINTS = [
  "Noise from front suspension",
  "AC cooling is low",
  "Brake pedal feels soft",
  "General service required",
  "Engine overheating",
  "Vehicle pulling to one side",
  "Check-engine light is on",
  "Vibration at high speed",
  "Poor pickup / power",
  "Battery not holding charge",
  "Steering feels heavy",
  "Clutch slipping",
];

// Task-picker categories used when the service-tasks master is empty.
export const TASK_CATEGORIES = [
  "General Service", "Engine", "Brake", "Suspension", "Steering", "Clutch",
  "Transmission", "Electrical", "Battery", "AC", "Cooling", "Exhaust",
  "Tyres", "Wheel Alignment", "Body", "Painting", "Inspection", "Detailing",
];

export const inr = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "-";
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n))}`;
};

// The set of quick-actions that can be shown based on the current status.
// One primary action rendered by the JC detail view — moves the JC forward
// through the sequential 8-state model.
export const PRIMARY_ACTION = {
  draft:              { key: "confirm",      label: "Create Job Card",      next: "vehicle_received"   },
  vehicle_received:   { key: "send_appr",    label: "Send for Approval",    next: "approval_pending"   },
  approval_pending:   { key: "record_appr",  label: "Record Approval",      next: "work_in_progress"   },
  work_in_progress:   { key: "start_qc",     label: "Send to Quality Check", next: "quality_check"     },
  quality_check:      { key: "ready_del",    label: "Ready for Delivery",   next: "ready_for_delivery" },
  ready_for_delivery: { key: "close",        label: "Close Job Card",       next: "closed"             },
  closed:             null,
  cancelled:          null,
};
